import React, { createContext, useContext, useReducer, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Basket context
const BasketContext = createContext();

// Basket reducer
const basketReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_BASKET':
      const payload = action.payload;
      const existingItem = state.items.find(item => item.id === payload.id);

      if (existingItem) {
        const updatedQuantity = existingItem.quantity + payload.quantity;
        return {
          ...state,
          items: state.items.map(item =>
            item.id === payload.id
              ? {
                  ...item,
                  quantity: updatedQuantity,
                  manage_stock: payload.manage_stock ?? item.manage_stock,
                  stock_quantity: payload.stock_quantity ?? item.stock_quantity,
                  stock_status: payload.stock_status ?? item.stock_status
                }
              : item
          ),
          totalItems: state.totalItems + payload.quantity,
          totalPrice: state.totalPrice + (existingItem.price * payload.quantity)
        };
      }

      return {
        ...state,
        items: [...state.items, payload],
        totalItems: state.totalItems + payload.quantity,
        totalPrice: state.totalPrice + (payload.price * payload.quantity)
      };

    case 'REMOVE_FROM_BASKET':
      const itemToRemove = state.items.find(item => item.id === action.payload);
      if (!itemToRemove) return state;
      
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
        totalItems: state.totalItems - itemToRemove.quantity,
        totalPrice: state.totalPrice - (itemToRemove.price * itemToRemove.quantity)
      };

    case 'UPDATE_QUANTITY':
      const itemToUpdate = state.items.find(item => item.id === action.payload.id);
      if (!itemToUpdate) return state;

      const desiredQuantityRaw = parseInt(action.payload.quantity, 10) || 0;
      const maxStock = itemToUpdate.manage_stock && Number.isFinite(itemToUpdate.stock_quantity)
        ? Math.max(itemToUpdate.stock_quantity, 0)
        : Infinity;
      const sanitizedQuantity = Math.max(1, Math.min(desiredQuantityRaw, maxStock));
      const quantityDiff = sanitizedQuantity - itemToUpdate.quantity;

      if (quantityDiff === 0) {
        return state;
      }

      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: sanitizedQuantity }
            : item
        ),
        totalItems: state.totalItems + quantityDiff,
        totalPrice: state.totalPrice + (itemToUpdate.price * quantityDiff)
      };

    case 'CLEAR_BASKET':
      return {
        items: [],
        totalItems: 0,
        totalPrice: 0
      };

    case 'LOAD_BASKET':
      return action.payload;

    default:
      return state;
  }
};

// Initial state
const initialState = {
  items: [],
  totalItems: 0,
  totalPrice: 0
};

const sanitizeBasketItem = (item) => {
  if (!item || typeof item !== 'object') return null;
  const manageStock = Boolean(item.manage_stock);
  const stockQuantityRaw = manageStock ? parseInt(item.stock_quantity, 10) : null;
  const stockQuantity = manageStock && Number.isFinite(stockQuantityRaw) ? Math.max(stockQuantityRaw, 0) : null;
  const quantity = Math.max(0, parseInt(item.quantity, 10) || 0);
  return {
    id: item.id,
    name: item.name,
    price: Number(item.price) || 0,
    originalPrice: Number(item.originalPrice ?? item.price) || 0,
    image: item.image || '/placeholder-product.jpg',
    quantity,
    slug: item.slug,
    sku: item.sku,
    manage_stock: manageStock,
    stock_quantity: stockQuantity,
    stock_status: item.stock_status || 'instock'
  };
};

const sanitizeBasketState = (rawState = initialState) => {
  if (!rawState || typeof rawState !== 'object') return initialState;
  const sanitizedItems = Array.isArray(rawState.items)
    ? rawState.items
        .map(sanitizeBasketItem)
        .filter(Boolean)
        .map(item => {
          if (item.manage_stock && Number.isFinite(item.stock_quantity)) {
            return { ...item, quantity: Math.min(item.quantity, item.stock_quantity) };
          }
          return item;
        })
        .filter(item => item.quantity > 0)
    : [];
  const totalItems = sanitizedItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = sanitizedItems.reduce((sum, item) => sum + item.quantity * item.price, 0);
  return { items: sanitizedItems, totalItems, totalPrice };
};

// Basket provider component
export const BasketProvider = ({ children }) => {
  const [state, dispatch] = useReducer(basketReducer, initialState);
  const location = useLocation();
  const hasLoadedForKeyRef = useRef(false);

  // Determine storage key based on current section/shop
  const storageKey = useMemo(() => {
    const path = location?.pathname || '';
    if (path.startsWith('/farm-shop') || path.includes('/retail-village/shops/farm-shop')) {
      return 'farmShopBasket';
    }
    if (path.startsWith('/duchess-collection') || path.includes('/retail-village/shops/duchess-collection')) {
      return 'duchessCollectionBasket';
    }
    return 'duchessCollectionBasket';
  }, [location]);

  // Load basket from localStorage when storageKey changes
  useEffect(() => {
    try {
      const savedBasket = localStorage.getItem(storageKey);
      if (savedBasket) {
        const parsedBasket = JSON.parse(savedBasket);
        dispatch({ type: 'LOAD_BASKET', payload: sanitizeBasketState(parsedBasket) });
      } else {
        dispatch({ type: 'LOAD_BASKET', payload: initialState });
      }
    } catch (error) {

      dispatch({ type: 'LOAD_BASKET', payload: initialState });
    } finally {
      hasLoadedForKeyRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Save basket to localStorage whenever it changes for the active storageKey
  useEffect(() => {
    if (!hasLoadedForKeyRef.current) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {

    }
  }, [state, storageKey]);

  // Basket actions
  const addToBasket = (product, quantity = 1) => {
    if (!product) {
      return { success: false, reason: 'invalid_product' };
    }

    const normalizedQuantity = Math.max(1, parseInt(quantity, 10) || 1);
    const stockStatus = product.stock_status || 'instock';
    const manageStock = Boolean(product.manage_stock);
    const stockQuantityRaw = manageStock ? parseInt(product.stock_quantity, 10) : null;
    const stockQuantity = manageStock && Number.isFinite(stockQuantityRaw) ? Math.max(stockQuantityRaw, 0) : null;

    if (stockStatus !== 'instock' || (manageStock && (stockQuantity === null || stockQuantity <= 0))) {
      return { success: false, reason: 'out_of_stock' };
    }

    const existingItem = state.items.find(item => item.id === product.id);
    const currentQuantity = existingItem ? existingItem.quantity : 0;
    const remaining = manageStock && stockQuantity !== null ? Math.max(stockQuantity - currentQuantity, 0) : Infinity;
    const allowedAdditional = manageStock && stockQuantity !== null ? Math.min(normalizedQuantity, remaining) : normalizedQuantity;

    if (allowedAdditional <= 0) {
      return { success: false, reason: 'limit_reached' };
    }

    const basketItem = {
      id: product.id,
      name: product.name,
      price: parseFloat(product.sale_price || product.price) || 0,
      originalPrice: parseFloat(product.regular_price || product.price) || 0,
      image: product.images?.[0]?.src || '/placeholder-product.jpg',
      quantity: allowedAdditional,
      slug: product.slug,
      sku: product.sku,
      manage_stock: manageStock,
      stock_quantity: stockQuantity,
      stock_status: stockStatus
    };

    dispatch({ type: 'ADD_TO_BASKET', payload: basketItem });

    return {
      success: true,
      addedQuantity: allowedAdditional,
      remaining: manageStock && stockQuantity !== null ? Math.max(0, remaining - allowedAdditional) : null
    };
  };

  const removeFromBasket = (productId) => {
    dispatch({ type: 'REMOVE_FROM_BASKET', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromBasket(productId);
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
    }
  };

  const clearBasket = () => {
    dispatch({ type: 'CLEAR_BASKET' });
  };

  const getItemQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  const isInBasket = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  const value = {
    ...state,
    addToBasket,
    removeFromBasket,
    updateQuantity,
    clearBasket,
    getItemQuantity,
    isInBasket
  };

  return (
    <BasketContext.Provider value={value}>
      {children}
    </BasketContext.Provider>
  );
};

// Custom hook to use basket context
export const useBasket = () => {
  const context = useContext(BasketContext);
  if (!context) {
    throw new Error('useBasket must be used within a BasketProvider');
  }
  return context;
};

export default BasketContext;